import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabasePublishableKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY

console.log('🔧 Supabase Config:', { 
  url: supabaseUrl, 
  hasKey: !!supabasePublishableKey
})

console.log('🔧 Environment Check:', {
  NODE_ENV: process.env.NODE_ENV,
  allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_') || key.startsWith('SUPABASE_')),
  supabaseUrl: supabaseUrl,
  supabaseKeyExists: !!supabasePublishableKey
})

// MIGRATION: Check if we have Supabase credentials, if not, we're in API-only mode
const hasSupabaseCredentials = !!(supabaseUrl && supabasePublishableKey)

if (!hasSupabaseCredentials) {
  console.log('🔧 Supabase: No frontend credentials found - running in API-only mode')
  console.log('🔧 Supabase: Database operations will use API endpoints instead of direct Supabase calls')
} else {
  console.log('🔧 Supabase: Frontend credentials found - direct database mode enabled')
  console.log('🔧 Supabase: Using browser deployment environment variables')
}

// Enhanced Supabase client configuration with optimized connection settings
const supabaseOptions = {
  auth: {
    autoRefreshToken: true,
    persistSession: false,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  db: {
    schema: 'public'
  },
  // Enhanced timeout and retry configuration
  realtime: {
    params: {
      eventsPerSecond: 10
    },
    heartbeatIntervalMs: 30000,
    reconnectAfterMs: function (tries) {
      return Math.min(tries * 1000, 30000)
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'e8-tracker/1.0'
    }
  }
}

// Create client conditionally - only if we have credentials
export const supabase = hasSupabaseCredentials 
  ? createClient(supabaseUrl, supabasePublishableKey, supabaseOptions)
  : null

// Export a flag to check if Supabase is available
export const isSupabaseAvailable = hasSupabaseCredentials

// Enhanced connection state tracking with detailed monitoring
let connectionState = {
  healthy: true,
  lastCheck: Date.now(),
  consecutiveFailures: 0,
  circuitBreakerOpen: false,
  lastError: null,
  // New monitoring fields
  totalRequests: 0,
  successfulRequests: 0,
  failedRequests: 0,
  averageResponseTime: 0,
  lastSuccessfulOperation: null,
  lastFailedOperation: null,
  operationHistory: [], // Keep last 10 operations
  performanceMetrics: {
    fastestRequest: null,
    slowestRequest: null,
    operationCounts: {}
  }
}

// Environment-specific configuration
const isDevelopment = process.env.NODE_ENV === 'development'

// Environment-specific timeout configuration
const TIMEOUT_CONFIG = {
  development: {
    default: 30000, // 30 seconds for development (more lenient)
    fetchUserProfile: 25000,
    getAllUsers: 35000,
    healthCheck: 20000,
    userOperations: 20000
  },
  production: {
    default: 20000, // 20 seconds for production
    fetchUserProfile: 20000,
    getAllUsers: 25000,
    healthCheck: 15000,
    userOperations: 15000
  }
}

// Get timeout value based on environment
const getTimeout = (operation = 'default') => {
  const config = isDevelopment ? TIMEOUT_CONFIG.development : TIMEOUT_CONFIG.production
  return config[operation] || config.default
}

// Circuit breaker configuration - Made less aggressive for better recovery
const CIRCUIT_BREAKER_THRESHOLD = isDevelopment ? 8 : 6 // More lenient in development
const CIRCUIT_BREAKER_TIMEOUT = isDevelopment ? 20000 : 15000 // Longer timeout in development
const HEALTH_CHECK_INTERVAL = 60000 // 1 minute between health checks

// Enhanced query wrapper with circuit breaker and exponential backoff
export const executeQuery = async (queryFn, operation = 'database operation', options = {}) => {
  // Check if Supabase is available
  if (!isSupabaseAvailable) {
    console.warn(`🔧 Supabase: ${operation} attempted but Supabase not available - should use API endpoint instead`)
    throw new Error('Supabase not available - use API endpoints instead')
  }

  let {
    timeout = getTimeout(operation), // Use environment-specific timeout
    retries = 2,
    retryDelay = 1000,
    skipCircuitBreaker = false
  } = options

  // Enhanced circuit breaker check with half-open state
  if (!skipCircuitBreaker && connectionState.circuitBreakerOpen) {
    const timeSinceOpen = Date.now() - connectionState.lastCheck
    if (timeSinceOpen < CIRCUIT_BREAKER_TIMEOUT) {
      // Check if this is a critical operation that should bypass circuit breaker
      const criticalOperations = ['fetchUserProfile', 'healthCheck']
      if (criticalOperations.includes(operation)) {
        console.log(`🔧 Supabase: Circuit breaker bypass for critical operation: ${operation}`)
        // CRITICAL FIX: Actually bypass the circuit breaker for critical operations
        // Continue with the operation instead of throwing an error
      } else {
        throw new Error(`Circuit breaker is open for ${operation}. Service temporarily unavailable.`)
      }
    } else {
      // Half-open state - try one request with reduced timeout
      console.log(`🔧 Supabase: Circuit breaker half-open, attempting ${operation} with reduced timeout`)
      connectionState.circuitBreakerOpen = false
      connectionState.consecutiveFailures = Math.max(0, connectionState.consecutiveFailures - 1) // Gradually reduce failure count
    }
  }

  let lastError
  
  // Pre-check: If connection is severely degraded, try a quick health check first
  if (connectionState.consecutiveFailures > 3 && !skipCircuitBreaker) {
    console.log(`🔧 Supabase: Connection degraded (${connectionState.consecutiveFailures} failures), performing pre-check...`)
    try {
      await checkConnectionHealth()
      console.log(`🔧 Supabase: Pre-check passed, proceeding with ${operation}`)
    } catch (preCheckError) {
      console.log(`🔧 Supabase: Pre-check failed, but continuing with ${operation}`)
    }
  }
  
  // Track operation start time for performance monitoring
  const operationStartTime = Date.now()
  connectionState.totalRequests++
  
  // Update operation count
  connectionState.performanceMetrics.operationCounts[operation] = 
    (connectionState.performanceMetrics.operationCounts[operation] || 0) + 1
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      console.log(`🔧 Supabase: Executing ${operation} (attempt ${attempt + 1}/${retries + 1}) with ${timeout}ms timeout`)
      
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`${operation} timed out after ${timeout}ms`))
        }, timeout)
      })

      // Execute query with timeout
      const result = await Promise.race([queryFn(), timeoutPromise])
      
      // Calculate operation time
      const operationTime = Date.now() - operationStartTime
      
      // Update performance metrics
      connectionState.successfulRequests++
      connectionState.lastSuccessfulOperation = operation
      connectionState.averageResponseTime = 
        (connectionState.averageResponseTime * (connectionState.successfulRequests - 1) + operationTime) / connectionState.successfulRequests
      
      // Track fastest/slowest requests
      if (!connectionState.performanceMetrics.fastestRequest || operationTime < connectionState.performanceMetrics.fastestRequest.time) {
        connectionState.performanceMetrics.fastestRequest = { operation, time: operationTime, timestamp: Date.now() }
      }
      if (!connectionState.performanceMetrics.slowestRequest || operationTime > connectionState.performanceMetrics.slowestRequest.time) {
        connectionState.performanceMetrics.slowestRequest = { operation, time: operationTime, timestamp: Date.now() }
      }
      
      // Add to operation history (keep last 10)
      connectionState.operationHistory.unshift({
        operation,
        success: true,
        time: operationTime,
        timestamp: Date.now(),
        attempts: attempt + 1
      })
      if (connectionState.operationHistory.length > 10) {
        connectionState.operationHistory.pop()
      }
      
      // Success - reset circuit breaker state
      connectionState.healthy = true
      connectionState.consecutiveFailures = 0
      connectionState.circuitBreakerOpen = false
      connectionState.lastCheck = Date.now()
      
      console.log(`🔧 Supabase: ${operation} completed successfully in ${operationTime}ms`)
      return result
      
    } catch (error) {
      lastError = error
      connectionState.lastError = error
      connectionState.consecutiveFailures++
      connectionState.failedRequests++
      connectionState.lastFailedOperation = operation
      
      // Calculate operation time for failed requests too
      const operationTime = Date.now() - operationStartTime
      
      // Add failed operation to history
      connectionState.operationHistory.unshift({
        operation,
        success: false,
        time: operationTime,
        timestamp: Date.now(),
        attempts: attempt + 1,
        error: error.message
      })
      if (connectionState.operationHistory.length > 10) {
        connectionState.operationHistory.pop()
      }
      
      console.error(`🔧 Supabase: ${operation} failed (attempt ${attempt + 1}) after ${operationTime}ms:`, error.message)
      
      // Check if we should open circuit breaker
      if (connectionState.consecutiveFailures >= CIRCUIT_BREAKER_THRESHOLD) {
        connectionState.circuitBreakerOpen = true
        connectionState.healthy = false
        connectionState.lastCheck = Date.now()
        console.error(`🔧 Supabase: Circuit breaker opened after ${connectionState.consecutiveFailures} failures`)
      }
      
      // Smart retry logic - don't retry on certain errors
      const nonRetryableErrors = [
        'not found',
        'unauthorized', 
        'forbidden',
        'invalid_grant',
        'access_denied',
        'invalid_request'
      ]
      
      const isNonRetryable = nonRetryableErrors.some(errorType => 
        error.message?.toLowerCase().includes(errorType)
      )
      
      if (isNonRetryable) {
        console.log(`🔧 Supabase: Non-retryable error detected: ${error.message}`)
        break
      }
      
      // For timeout errors, reduce timeout for retry attempts
      if (error.message?.includes('timed out') && attempt < retries) {
        console.log(`🔧 Supabase: Timeout detected, reducing timeout for retry`)
        timeout = Math.max(timeout * 0.8, 5000) // Reduce timeout by 20%, minimum 5s
      }
      
      // Enhanced exponential backoff with jitter and operation-specific delays
      if (attempt < retries) {
        // Base delay with exponential backoff
        const baseDelay = retryDelay * Math.pow(2, attempt)
        
        // Add jitter to prevent thundering herd
        const jitter = Math.random() * 1000
        
        // Add operation-specific multiplier for critical operations
        const operationMultiplier = operation === 'fetchUserProfile' ? 1.5 : 1.0
        
        const finalDelay = (baseDelay * operationMultiplier) + jitter
        
        console.log(`🔧 Supabase: Retrying ${operation} in ${Math.round(finalDelay)}ms... (attempt ${attempt + 1}/${retries + 1})`)
        await new Promise(resolve => setTimeout(resolve, finalDelay))
      }
    }
  }
  
  connectionState.healthy = false
  throw lastError
}

// Enhanced connection health check
export const checkConnectionHealth = async () => {
  if (!isSupabaseAvailable) {
    console.log('🔧 Supabase: Health check skipped - not available in production')
    return false
  }

  try {
    console.log('🔧 Supabase: Performing connection health check...')
    
    // Simple ping to the REST API
    const healthCheck = supabase
      .from('user_profiles')
      .select('count', { count: 'exact', head: true })
      .limit(1)
    
    await executeQuery(() => healthCheck, 'healthCheck', { 
      timeout: getTimeout('healthCheck'), // Use environment-specific timeout
      retries: 1,
      skipCircuitBreaker: true 
    })
    
    connectionState.healthy = true
    connectionState.lastCheck = Date.now()
    console.log('🔧 Supabase: Connection health check passed')
    return true
    
  } catch (error) {
    connectionState.healthy = false
    connectionState.lastCheck = Date.now()
    console.error('🔧 Supabase: Connection health check failed:', error.message)
    return false
  }
}

// Get current connection state
export const getConnectionState = () => ({
  ...connectionState,
  age: Date.now() - connectionState.lastCheck
})

// Get detailed connection metrics for debugging
export const getConnectionMetrics = () => ({
  ...connectionState,
  age: Date.now() - connectionState.lastCheck,
  successRate: connectionState.totalRequests > 0 ? 
    (connectionState.successfulRequests / connectionState.totalRequests * 100).toFixed(2) + '%' : 'N/A',
  recentOperations: connectionState.operationHistory.slice(0, 5), // Last 5 operations
  performanceSummary: {
    averageResponseTime: Math.round(connectionState.averageResponseTime),
    fastestRequest: connectionState.performanceMetrics.fastestRequest,
    slowestRequest: connectionState.performanceMetrics.slowestRequest,
    operationCounts: connectionState.performanceMetrics.operationCounts
  }
})

// Reset circuit breaker (useful when previous failures were due to implementation errors)
export const resetCircuitBreaker = () => {
  console.log('🔧 Supabase: Manually resetting circuit breaker state')
  connectionState.healthy = true
  connectionState.consecutiveFailures = 0
  connectionState.circuitBreakerOpen = false
  connectionState.lastError = null
  connectionState.lastCheck = Date.now()
  return connectionState
}

// Alternative health check that bypasses circuit breaker completely (for diagnostics)
export const checkConnectionHealthDirect = async () => {
  if (!isSupabaseAvailable) {
    console.log('🔧 Supabase: Direct health check skipped - not available in production')
    return false
  }

  try {
    console.log('🔧 Supabase: Performing direct connection health check (bypassing circuit breaker)...')
    
    // Direct fetch to REST API without circuit breaker
    const response = await fetch(`${supabaseUrl}/rest/v1/user_profiles?select=count&limit=1`, {
      method: 'GET',
      headers: {
        'apikey': supabasePublishableKey,
        'Content-Type': 'application/json',
        'Prefer': 'count=exact'
      },
      signal: AbortSignal.timeout(5000) // 5 second timeout
    })
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    const result = await response.text() // Get response text for debugging
    console.log('🔧 Supabase: Direct health check passed:', { status: response.status, result })
    
    // Reset circuit breaker on successful direct check
    resetCircuitBreaker()
    
    return true
    
  } catch (error) {
    console.error('🔧 Supabase: Direct health check failed:', error.message)
    return false
  }
}

// Test function to bypass timeout wrapper and test raw Supabase client
export const testRawSupabaseClient = async () => {
  if (!isSupabaseAvailable) {
    console.log('🔧 Supabase: Raw client test skipped - not available in production')
    return { success: false, error: 'Supabase not available' }
  }

  try {
    console.log('🔧 Supabase: Testing raw Supabase client (no timeout wrapper)...')
    
    // First, verify the client configuration
    console.log('🔧 Supabase: Current client config:', {
      url: supabaseUrl,
      hasKey: !!supabasePublishableKey,
      clientExists: !!supabase
    })
    
    // Test the raw Supabase client without any timeout wrapper
    const { data, error } = await supabase
      .from('user_profiles')
      .select('count')
      .limit(1)
    
    if (error) {
      console.error('🔧 Supabase: Raw client test failed:', error)
      return { success: false, error: error.message }
    }
    
    console.log('🔧 Supabase: Raw client test passed:', data)
    return { success: true, data }
    
  } catch (error) {
    console.error('🔧 Supabase: Raw client test exception:', error)
    return { success: false, error: error.message }
  }
}

// Periodic health monitoring (runs every minute) - only if Supabase is available
if (typeof window !== 'undefined' && isSupabaseAvailable) {
  setInterval(async () => {
    // Only check if we haven't checked recently or if connection is unhealthy
    const timeSinceLastCheck = Date.now() - connectionState.lastCheck
    if (timeSinceLastCheck > HEALTH_CHECK_INTERVAL || !connectionState.healthy) {
      await checkConnectionHealth()
    }
  }, HEALTH_CHECK_INTERVAL)
} else if (typeof window !== 'undefined') {
  console.log('🔧 Supabase: Periodic health monitoring disabled - running in API-only mode')
}
